import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { coerce, gte, valid } from 'semver'
import { yupResolver } from '@hookform/resolvers/yup'
import { format, parse, parseISO } from 'date-fns'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  type Resolver,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { boolean, number, object, string, type AnySchema } from 'yup'
import type { Device } from '@/pages/Device/Device'
import {
  type Ba,
  type CalibrationData,
  formatBa,
  type ResistanceBox,
  resistanceBoxLabel,
} from '../calibration'
import type { Composition } from '../composition'
import { ZKL_3000_RC, ZKL_3000_RC_AFTC } from '@/pages/Device/SupportedTypes'
import { getZklRcCalibration, updateMustHaveAftc } from '@/api/calibration'
import useResponseValidator from '@/pages/utils/responseValidator'
import { CalibrationSchema } from '../schema'
import PreFlightStepBase from './PreFlightStepBase'
import {
  formLegendSx,
  labelNoErrorColor,
  labelRoot,
  radioGroupLabelSx,
  requiredIndicator,
  selectStandardSx,
} from '@/utils/theme'
import {
  DATE_FORMAT,
  resolveDeviceRole,
  usePreFlightBase,
} from '@/hooks/usePreFlightBase'
import SharedPreFlightFields, {
  showFieldError,
} from '@/components/SharedPreFlightFields'
import { isNmdiWithFota } from '@/pages/calibration/version'

const RESISTANCE_DROP_OFF = 10
const ZKL_AFTC_TYPES = new Set<string>([ZKL_3000_RC, ZKL_3000_RC_AFTC])

interface Props {
  device: Device
  setDevice?: (device: Device) => void
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  resistanceBox: ResistanceBox | null
  setResistanceBox: (rb: ResistanceBox | null) => void
  jumperBox: string | null
  setJumperBox: (jb: string | null) => void
  ba: Ba
  setBa: (ba: Ba) => void
  dropOff: string
  setDropOff: (d: string) => void
  setActiveStep: (step: number) => void
  onNext: () => void
}

type FormValues = {
  country: string
  deviceRoleCode: string
  installedDate: string
  yearsBetweenMaintenance: string
  certificateUploaded: boolean
  resistanceBox: string
  jumperBox: string
  baValue: number
  dropOff: string
  acceptExcessiveBaValue: boolean
  mustHaveAftc: string
  diversityAntennaCheck: string
  mainHardwarePatched: boolean
  switchHardwarePatched: boolean
}

interface ZklRcExtraFieldsProps {
  resistanceBoxes: ResistanceBox[]
  jumperBoxes: string[]
  canHaveAftc: boolean
  composition: Composition | null
  onResistanceBoxChange: (label: string) => void
  onJumperBoxChange: (value: string) => void
  isNmdiPatch: boolean
  isSubmitted: boolean
}

const ZklRcExtraFields = ({
  resistanceBoxes,
  jumperBoxes,
  canHaveAftc,
  composition,
  onResistanceBoxChange,
  onJumperBoxChange,
  isNmdiPatch,
  isSubmitted,
}: ZklRcExtraFieldsProps): JSX.Element => {
  const { t } = useTranslation(['app', 'common'])
  const { control } = useFormContext<FormValues>()
  const ncmVersion = coerce(composition?.composition.ncmFirmware ?? '')
  const hasValidNcmVersion = valid(ncmVersion) && ncmVersion !== null
  const showMainHardwarePatch =
    isNmdiPatch ||
    composition?.composition.mainHardware === '2.6.1' ||
    composition?.composition.switchHardware === '2.7.1'

  return (
    <>
      {canHaveAftc && (
        <Controller
          name="mustHaveAftc"
          control={control}
          render={({ field, fieldState }) => {
            const hasError = isSubmitted && Boolean(fieldState.error)
            return (
              <FormControl
                component="fieldset"
                error={hasError}
                sx={{ mb: 2.5, width: '100%', display: 'block' }}
              >
                <FormLabel
                  component="legend"
                  sx={{ ...formLegendSx, ...requiredIndicator }}
                >
                  {t('app:device.calibration.preFlightStep.aftcPresent')}
                </FormLabel>
                <RadioGroup {...field}>
                  {[t('common:general.yes'), t('common:general.no')].map(
                    (opt) => (
                      <FormControlLabel
                        key={opt}
                        value={opt}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: hasError
                                ? 'error.main'
                                : 'rgba(0, 0, 0, 0.54)',
                              '&.Mui-checked': {
                                color: hasError
                                  ? 'error.main'
                                  : 'rgba(0, 0, 0, 0.87)',
                              },
                            }}
                          />
                        }
                        label={opt}
                      />
                    ),
                  )}
                </RadioGroup>
                {isSubmitted && showFieldError(fieldState) && (
                  <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
                )}
              </FormControl>
            )
          }}
        />
      )}

      {hasValidNcmVersion && (
        <Controller
          name="diversityAntennaCheck"
          control={control}
          render={({ field, fieldState }) => {
            const hasError = isSubmitted && Boolean(fieldState.error)
            return (
              <FormControl
                component="fieldset"
                error={hasError}
                sx={{ mb: 2.5, width: '100%', display: 'block' }}
              >
                <FormLabel
                  component="legend"
                  sx={{ ...formLegendSx, ...requiredIndicator }}
                >
                  {t(
                    'app:device.calibration.preFlightStep.diversityAntennaCheck',
                  )}
                </FormLabel>
                <RadioGroup {...field}>
                  {[t('common:general.yes'), t('common:general.no')].map(
                    (opt) => (
                      <FormControlLabel
                        key={opt}
                        value={opt}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: hasError
                                ? 'error.main'
                                : 'rgba(0, 0, 0, 0.54)',
                              '&.Mui-checked': {
                                color: hasError
                                  ? 'error.main'
                                  : 'rgba(0, 0, 0, 0.87)',
                              },
                            }}
                          />
                        }
                        label={opt}
                      />
                    ),
                  )}
                </RadioGroup>
                {isSubmitted && showFieldError(fieldState) && (
                  <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
                )}
              </FormControl>
            )
          }}
        />
      )}

      <FormGroup sx={{ mb: 3 }}>
        <Typography sx={radioGroupLabelSx}>
          {t('app:device.calibration.preFlightStep.zklRc.selectUsedTools')}
        </Typography>
        <Controller
          name="resistanceBox"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={isSubmitted && Boolean(fieldState.error)}
              sx={{ mt: 1, mb: 1 }}
            >
              <InputLabel
                variant="standard"
                sx={{
                  ...labelRoot,
                  ...requiredIndicator,
                  ...labelNoErrorColor,
                }}
              >
                {t('app:device.calibration.zklRc.resistanceBox')}
              </InputLabel>
              <Select
                {...field}
                variant="standard"
                sx={selectStandardSx}
                onChange={(e) => {
                  field.onChange(e)
                  onResistanceBoxChange(e.target.value)
                }}
              >
                {resistanceBoxes.map((rb) => (
                  <MenuItem key={rb.label} value={rb.label}>
                    {resistanceBoxLabel(rb)}
                  </MenuItem>
                ))}
              </Select>
              {isSubmitted && showFieldError(fieldState) && (
                <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="jumperBox"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={isSubmitted && Boolean(fieldState.error)}
              sx={{ mb: 1 }}
            >
              <InputLabel
                variant="standard"
                sx={{
                  ...labelRoot,
                  ...requiredIndicator,
                  ...labelNoErrorColor,
                }}
              >
                {t('app:device.calibration.zklRc.switchBox')}
              </InputLabel>
              <Select
                {...field}
                variant="standard"
                sx={selectStandardSx}
                onChange={(e) => {
                  field.onChange(e)
                  onJumperBoxChange(e.target.value)
                }}
              >
                {jumperBoxes.map((jb) => (
                  <MenuItem key={jb} value={jb}>
                    {jb}
                  </MenuItem>
                ))}
              </Select>
              {isSubmitted && showFieldError(fieldState) && (
                <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </FormGroup>

      <Controller
        name="baValue"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            label={t('app:device.calibration.zklRc.baAutocalValue')}
            fullWidth
            variant="standard"
            disabled
            sx={{ mb: 2.5 }}
          />
        )}
      />

      <Controller
        name="dropOff"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('app:device.calibration.zklRc.calibrationDropOff')}
            fullWidth
            variant="standard"
            disabled
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">{'<'}</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">mOhm</InputAdornment>
                ),
              },
            }}
            sx={{ mb: 2.5 }}
          />
        )}
      />

      <Controller
        name="acceptExcessiveBaValue"
        control={control}
        render={({ field, fieldState }) => {
          const hasError = isSubmitted && Boolean(fieldState.error)
          return (
            <FormControl error={hasError} sx={{ display: 'block' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{
                      color: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.54)',
                      '&.Mui-checked': {
                        color: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
                      },
                    }}
                  />
                }
                label={
                  <Box
                    component="span"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {t(
                      'app:device.calibration.preFlightStep.zklRc.acceptExcessiveBaValue',
                    )}
                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                      *
                    </Box>
                  </Box>
                }
              />
              {isSubmitted && showFieldError(fieldState) && (
                <FormHelperText sx={{ ml: 4 }}>
                  {showFieldError(fieldState)}
                </FormHelperText>
              )}
            </FormControl>
          )
        }}
      />

      {showMainHardwarePatch && (
        <Box>
          <Controller
            name="mainHardwarePatched"
            control={control}
            render={({ field, fieldState }) => {
              const hasError = isSubmitted && Boolean(fieldState.error)
              return (
                <FormControl error={hasError} sx={{ display: 'block' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{
                          color: hasError
                            ? 'error.main'
                            : 'rgba(0, 0, 0, 0.54)',
                          '&.Mui-checked': {
                            color: hasError
                              ? 'error.main'
                              : 'rgba(0, 0, 0, 0.87)',
                          },
                        }}
                      />
                    }
                    label={
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        {t(
                          'app:device.calibration.preFlightStep.zklRc.nmdiCustomMainHardwareCheckBox',
                        )}
                        <Box
                          component="span"
                          sx={{ color: 'error.main', ml: 0.5 }}
                        >
                          *
                        </Box>
                      </Box>
                    }
                  />
                  {isSubmitted && showFieldError(fieldState) && (
                    <FormHelperText sx={{ ml: 4 }}>
                      {showFieldError(fieldState)}
                    </FormHelperText>
                  )}
                </FormControl>
              )
            }}
          />
          <Controller
            name="switchHardwarePatched"
            control={control}
            render={({ field, fieldState }) => {
              const hasError = isSubmitted && Boolean(fieldState.error)
              return (
                <FormControl error={hasError} sx={{ display: 'block' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{
                          color: hasError
                            ? 'error.main'
                            : 'rgba(0, 0, 0, 0.54)',
                          '&.Mui-checked': {
                            color: hasError
                              ? 'error.main'
                              : 'rgba(0, 0, 0, 0.87)',
                          },
                        }}
                      />
                    }
                    label={
                      <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        {t(
                          'app:device.calibration.preFlightStep.zklRc.nmdiCustomSwitchCheckBox',
                        )}
                        <Box
                          component="span"
                          sx={{ color: 'error.main', ml: 0.5 }}
                        >
                          *
                        </Box>
                      </Box>
                    }
                  />
                  {isSubmitted && showFieldError(fieldState) && (
                    <FormHelperText sx={{ ml: 4 }}>
                      {showFieldError(fieldState)}
                    </FormHelperText>
                  )}
                </FormControl>
              )
            }}
          />
        </Box>
      )}
    </>
  )
}

const ZklrcPreFlightStep = ({
  device,
  setDevice,
  setComposition,
  resistanceBox,
  setResistanceBox,
  jumperBox,
  setJumperBox,
  ba,
  setBa,
  dropOff,
  setDropOff,
  setActiveStep,
  onNext,
}: Props): JSX.Element => {
  const { t } = useTranslation(['app', 'common'])
  const { validate } = useResponseValidator()
  const [resistanceBoxes, setResistanceBoxes] = useState<ResistanceBox[]>([])
  const [jumperBoxes, setJumperBoxes] = useState<string[]>([])

  const canHaveAftc = ZKL_AFTC_TYPES.has(device.type)
  const hasConfigurableServiceInterval = ZKL_AFTC_TYPES.has(device.type)

  const {
    composition,
    refreshComposition,
    deviceRoles,
    deviceRolesLoading,
    isSaving,
    effectiveCountry,
    effectiveYears,
    countryItems,
    hasBoard,
    minInstalledDate,
    deviceRoleRequired,
    submitForm,
    handleCountryChange,
    handleYearsIntervalChange,
  } = usePreFlightBase(device, setComposition, onNext)

  const isNmdiPatch = Boolean(
    ((composition?.composition.ncmHardware === '1.3.0' &&
      composition?.composition.ncmFirmware === '1.1.0' &&
      (composition?.composition.switchHardware === '2.7' ||
        composition?.composition.mainHardware === '2.6') &&
      device.country === 'NL') ||
      device.country === 'FR') &&
    composition?.ncmType === 'nmdi',
  )

  const ncmVersion = coerce(composition?.composition.ncmFirmware ?? '')
  const compositionDeviceRoleCode =
    composition?.composition?.deviceRoleCode ?? ''
  const requiresCertificateUpload = isNmdiWithFota(composition)

  const validateRef = useRef(validate)
  useEffect(() => {
    validateRef.current = validate
  })

  const setBaRef = useRef(setBa)
  useEffect(() => {
    setBaRef.current = setBa
  })

  const fetchCalibration = useCallback(async () => {
    try {
      const response = await getZklRcCalibration(device.uid)
      const { resistances, calibration, jumpers } =
        await validateRef.current<CalibrationData>(CalibrationSchema, response)
      setResistanceBoxes(resistances)
      setJumperBoxes(jumpers)
      setBaRef.current(calibration.ba)
    } catch {
      // Caught by interceptor
    }
  }, [device.uid])

  useEffect(() => {
    void fetchCalibration()
  }, [fetchCalibration])

  const handleResistanceBoxChange = useCallback(
    (label: string) => {
      const found = resistanceBoxes.find((rb) => rb.label === label)
      if (found) {
        setDropOff(String(found.mOhm + RESISTANCE_DROP_OFF))
        setResistanceBox(found)
      }
    },
    [resistanceBoxes, setDropOff, setResistanceBox],
  )

  const handleJumperBoxChange = useCallback(
    (value: string) => {
      const found = jumperBoxes.find((jb) => jb === value)
      if (found !== undefined) setJumperBox(found)
    },
    [jumperBoxes, setJumperBox],
  )

  const schema = useMemo(() => {
    const shape: Record<string, AnySchema> = {
      country: string().required(),
      deviceRoleCode: deviceRoleRequired
        ? string().required(
            t('app:device.calibration.preFlightStep.deviceRoleRequired'),
          )
        : string().optional(),
      installedDate: hasBoard
        ? string()
            .required()
            .test(
              'min-date',
              t('app:device.calibration.preFlightStep.replaceBattery'),
              (value) => {
                if (!value) return false
                return parse(value, DATE_FORMAT, new Date()) >= minInstalledDate
              },
            )
            .test('max-date', 'Date cannot be in the future', (value) => {
              if (!value) return false
              return parse(value, DATE_FORMAT, new Date()) <= new Date()
            })
        : string().optional(),
      yearsBetweenMaintenance: hasConfigurableServiceInterval
        ? string().oneOf(['2', '4']).required()
        : string().optional(),
      certificateUploaded: requiresCertificateUpload
        ? boolean().oneOf(
            [true],
            'Certificate upload must be completed before continuing.',
          )
        : boolean().optional(),
      resistanceBox: string().required(),
      jumperBox: string().required(),
      baValue: number().required(),
      acceptExcessiveBaValue: boolean().required().equals([true], ''),
      dropOff: string().optional(),
      mustHaveAftc: string().optional(),
      diversityAntennaCheck: string().optional(),
      mainHardwarePatched: boolean().optional(),
      switchHardwarePatched: boolean().optional(),
    }

    if (isNmdiPatch) {
      shape.mainHardwarePatched = boolean().equals([true], '').required()
      shape.switchHardwarePatched = boolean().equals([true], '').required()
    }

    if (canHaveAftc) {
      const expectedAftc =
        device.type === ZKL_3000_RC_AFTC
          ? t('common:general.yes')
          : t('common:general.no')
      shape.mustHaveAftc = string()
        .required()
        .test(
          'aftc-correct-value',
          t('app:device.calibration.preFlightStep.aftcBoardError'),
          (value) => {
            if (!value) return true
            return value === expectedAftc
          },
        )
    }

    if (valid(ncmVersion) && ncmVersion !== null) {
      const diversityRequired =
        (gte(ncmVersion, '2.1.4') && composition?.ncmType === 'dncm') ||
        (gte(ncmVersion, '1.1.0') && composition?.ncmType === 'nmdi')
      const expectedDiversity = diversityRequired
        ? t('common:general.yes')
        : t('common:general.no')
      const diversityErrorMessage = diversityRequired
        ? t(
            'app:device.calibration.preFlightStep.diversityAntennaNotInstalledError',
            { type: composition?.ncmType?.toUpperCase() ?? '' },
          )
        : t(
            'app:device.calibration.preFlightStep.diversityAntennaInstalledError',
          )
      shape.diversityAntennaCheck = string()
        .required()
        .test('diversity-correct-value', diversityErrorMessage, (value) => {
          if (!value) return true
          return value === expectedDiversity
        })
    }

    return object(shape)
  }, [
    hasBoard,
    hasConfigurableServiceInterval,
    isNmdiPatch,
    canHaveAftc,
    device.type,
    deviceRoleRequired,
    requiresCertificateUpload,
    ncmVersion,
    composition?.ncmType,
    minInstalledDate,
    t,
  ])

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<FormValues>,
    defaultValues: {
      country: effectiveCountry,
      deviceRoleCode: compositionDeviceRoleCode,
      installedDate:
        hasBoard && device.batteryBoardDate
          ? format(parseISO(device.batteryBoardDate), DATE_FORMAT)
          : '',
      yearsBetweenMaintenance: hasConfigurableServiceInterval
        ? String(effectiveYears)
        : '',
      certificateUploaded: false,
      resistanceBox: resistanceBox?.label ?? '',
      jumperBox: jumperBox ?? '',
      baValue: formatBa(ba),
      dropOff,
      acceptExcessiveBaValue: false,
      mustHaveAftc: '',
      diversityAntennaCheck: '',
      mainHardwarePatched: false,
      switchHardwarePatched: false,
    },
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  useEffect(() => {
    methods.setValue('baValue', formatBa(ba), { shouldValidate: true })
  }, [ba, methods])

  useEffect(() => {
    methods.setValue('dropOff', dropOff, { shouldValidate: true })
  }, [dropOff, methods])

  useEffect(() => {
    if (deviceRoles.length === 0) return
    methods.setValue(
      'deviceRoleCode',
      resolveDeviceRole(deviceRoles, compositionDeviceRoleCode),
      { shouldValidate: true },
    )
  }, [compositionDeviceRoleCode, deviceRoles, methods])

  const { serial, label, type } = device

  return (
    <FormProvider {...methods}>
      <PreFlightStepBase
        serial={serial ?? ''}
        label={label}
        type={type}
        isSaving={isSaving}
        composition={composition}
        onRefreshComposition={refreshComposition}
        onBack={() => setActiveStep(0)}
        onFormSubmit={(e, compositionCheckPasses) => {
          setHasAttemptedSubmit(true)
          void methods.handleSubmit(async (values) => {
            if (!compositionCheckPasses) return
            await submitForm({
              country: values.country,
              deviceRoleCode: values.deviceRoleCode,
              yearsBetweenMaintenance: hasConfigurableServiceInterval
                ? values.yearsBetweenMaintenance
                : undefined,
              installedDate: hasBoard ? values.installedDate : undefined,
              onExtraSubmit: canHaveAftc
                ? async () => {
                    await updateMustHaveAftc(
                      device.uid,
                      values.mustHaveAftc === t('common:general.yes'),
                    )
                  }
                : undefined,
            })
          })(e)
        }}
      >
        <SharedPreFlightFields
          control={methods.control}
          isSubmitted={hasAttemptedSubmit}
          hasBoard={hasBoard}
          minInstalledDate={minInstalledDate}
          countryItems={countryItems}
          deviceRoles={deviceRoles}
          deviceRolesLoading={deviceRolesLoading}
          hasConfigurableServiceInterval={hasConfigurableServiceInterval}
          isSaving={isSaving}
          onCountryChange={handleCountryChange}
          onYearsIntervalChange={handleYearsIntervalChange}
          composition={composition}
          device={device}
          setDevice={setDevice}
          certificateUploadFieldName="certificateUploaded"
        />
        <ZklRcExtraFields
          resistanceBoxes={resistanceBoxes}
          jumperBoxes={jumperBoxes}
          canHaveAftc={canHaveAftc}
          composition={composition}
          onResistanceBoxChange={handleResistanceBoxChange}
          onJumperBoxChange={handleJumperBoxChange}
          isNmdiPatch={isNmdiPatch}
          isSubmitted={hasAttemptedSubmit}
        />
      </PreFlightStepBase>
    </FormProvider>
  )
}

export default ZklrcPreFlightStep
